import type { insights as InsightsApi } from '@cwos/api-client';

type FindingRequirementClass = InsightsApi.FindingRequirementClass;

/**
 * The words for the applicability write control, kept out of the component so
 * a unit test can hold them without a DOM.
 */

/**
 * THE SIX CLASSES, IN THE ORDER A PERSON THINKS ABOUT THEM, AND ALL SIX ARE
 * OFFERED.
 *
 * `platform_constraint` is deliberately in the list even though a suppressing
 * ruling against it is refused for everybody. Hiding it would teach a
 * compliance officer that the product has no opinion about Phibian's own
 * execution envelope, when the opinion is the point: the refusal names a
 * separate resolution authority and says a customer cannot waive, supersede or
 * retire one. A person who reaches for it should be told that, not find the
 * option missing.
 */
export const REQUIREMENT_CLASS_CHOICES: readonly {
  value: FindingRequirementClass;
  label: string;
}[] = [
  { value: 'regulatory', label: 'A law, regulation or licensing requirement' },
  { value: 'contractual', label: 'A payer contract, network or program requirement' },
  { value: 'customer_policy', label: 'This organization’s own policy' },
  { value: 'operational_fact', label: 'Something this organization has established about itself' },
  { value: 'phibian_doctrine', label: 'Phibian’s operating advice' },
  { value: 'platform_constraint', label: 'A Phibian safety control' },
];

/**
 * The server's two governing-floor refusals, as sentences.
 *
 * THESE ARE NOT PERMISSION PROBLEMS AND THE COPY MUST NOT IMPLY ONE. Both
 * arrive as 403, and a screen that rendered "Forbidden" or "ask an
 * administrator" would send somebody looking for a grant that does not exist —
 * no role, and no direct grant, makes either act possible. The server marks
 * them with `refusal` rather than `permission` precisely so the two can be
 * told apart here.
 */
export function floorRefusalSentence(error: unknown): string {
  const refusal =
    typeof error === 'object' && error !== null && 'body' in error
      ? refusalOf((error as { body?: unknown }).body)
      : refusalOf(error);
  switch (refusal) {
    case 'platform_safety_control':
      return (
        'A Phibian safety control is not a requirement your organization is under — it is a ' +
        'limit on what Phibian itself will do — so it is not something an applicability ruling ' +
        'can set aside. Nobody in your organization can, and no permission changes that. You ' +
        'can still record that it applies, or that you could not tell.'
      );
    case 'governing_effective_window':
      return (
        'Whether a law or a contract is yet in force, or has been superseded, comes from the ' +
        'source’s own effective dates rather than from a judgement here. You can record that ' +
        'the requirement does not govern this program, and the ruling will end on its own when ' +
        'the source’s window closes.'
      );
    default:
      return messageOf(error) ?? 'That determination could not be recorded.';
  }
}

function refusalOf(value: unknown): string | null {
  if (typeof value === 'object' && value !== null && 'refusal' in value) {
    const refusal = (value as { refusal?: unknown }).refusal;
    return typeof refusal === 'string' ? refusal : null;
  }
  return null;
}

function messageOf(error: unknown): string | null {
  if (typeof error === 'object' && error !== null && 'message' in error) {
    const message = (error as { message?: unknown }).message;
    return typeof message === 'string' && message.length > 0 ? message : null;
  }
  return null;
}
